/**
 * Cash Flow Forecasting - on-demand per-client
 *
 * Previously this page scanned EVERY client on load (12 Xero P&L calls per client
 * × 600 clients = 7,200+ API calls). That's not viable at firm scale.
 *
 * New approach: load a lightweight client list (DB query only, no Xero),
 * let the accountant search and pick one client, then fetch the forecast
 * for that client on demand.
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Minus, Loader2, BarChart3, ArrowRight, Search, Info, Link2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Legend, ReferenceLine } from 'recharts';

const fmtCurrency = (v: number) => `$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const TREND_STYLES: Record<string, { label: string; icon: any; color: string }> = {
  growing: { label: 'Growing', icon: TrendingUp, color: 'text-green-500' },
  stable: { label: 'Stable', icon: Minus, color: 'text-blue-500' },
  declining: { label: 'Declining', icon: TrendingDown, color: 'text-red-500' },
};

export default function CashFlowForecastPage() {
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);
  const [search, setSearch] = useState('');

  // Lightweight client list - DB only, no Xero calls. Cheap.
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients-for-cashflow'],
    queryFn: async () => { const r = await apiClient.get('/clients?limit=1000'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  // Per-client forecast - only fetched when a client is picked
  const { data: clientDetail, isLoading: detailLoading, error: detailError } = useQuery({
    queryKey: ['cashflow-detail', selectedClient?.id],
    queryFn: async () => { const r = await apiClient.get(`/compliance/cashflow/${selectedClient!.id}`); return r.data.data; },
    enabled: !!selectedClient,
  });

  // Build the pickable list: only Xero-connected active clients
  const allClients = (clientsData?.clients || clientsData || []) as any[];
  // A client is forecastable if connected to ANY accounting system (Xero or QBO).
  const hasAccounting = (c: any) => !!(c.xeroOrgId || c.xero_org_id || c.qbo_realm_id || c.qboRealmId);
  const xeroClients = useMemo(() => {
    return allClients
      .filter(c => hasAccounting(c) && (c.status === 'active' || !c.status))
      .map(c => ({
        id: c.id,
        name: c.displayName || c.display_name,
        industry: c.industry,
        provider: (c.qbo_realm_id || c.qboRealmId) && !(c.xeroOrgId || c.xero_org_id) ? 'QuickBooks' : 'Xero',
      }));
  }, [allClients]);

  const filteredClients = useMemo(() => {
    if (!search.trim()) return xeroClients;
    const q = search.toLowerCase();
    return xeroClients.filter(c => c.name?.toLowerCase().includes(q) || c.industry?.toLowerCase().includes(q));
  }, [xeroClients, search]);

  const clientsWithoutXero = allClients.filter(c => !hasAccounting(c)).length;

  // Prepare chart data for selected client
  const chartData = clientDetail ? [
    ...(clientDetail.historical || []).map((h: any) => ({
      month: h.label,
      revenue: h.revenue,
      expenses: h.expenses,
      net: h.netCashFlow,
      projected: false,
    })),
    ...(clientDetail.projected || []).map((p: any) => ({
      month: p.label,
      revenue: p.revenue,
      expenses: p.expenses,
      net: p.netCashFlow,
      confidenceLow: p.confidenceLow,
      confidenceHigh: p.confidenceHigh,
      projected: true,
    })),
  ] : [];

  const insufficientData = clientDetail && (clientDetail.historical?.length || 0) < 3;

  // Label of the first projected month - used to draw the "today / forecast start" line
  const forecastStartLabel: string | null = clientDetail?.projected?.[0]?.label || null;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BarChart3}
        title="Cash Flow Forecasting"
        description="6-month projection from 12 months of Xero or QuickBooks P&L history"
      />

      {/* Overview strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold">{allClients.length}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Clients</p>
          </CardContent>
        </Card>
        <Card className="border-blue-500/30">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{xeroClients.length}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Accounting Connected</p>
          </CardContent>
        </Card>
        <Card className="border-amber-500/30">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{clientsWithoutXero}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Without Accounting</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold">12 + 6</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Months History + Forecast</p>
          </CardContent>
        </Card>
      </div>

      {/* Data source note - always visible */}
      <div className="p-3 rounded-md bg-blue-500/5 border border-blue-500/20 flex items-start gap-2 text-[11px] text-muted-foreground leading-relaxed">
        <Info className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
        <div>
          <strong className="text-foreground">How this works: </strong>
          Pick a client to generate a forecast on-demand. We pull 12 months of monthly P&L from Xero or QuickBooks (≈12 API calls per client),
          compute a weighted moving average for revenue and expenses, and project 6 months forward with a confidence band.
          Clients without an accounting connection can\'t be forecasted - connect Xero or QuickBooks from the client page.
          Uploaded files are NOT used for forecasting.
        </div>
      </div>

      {/* Picker + Detail layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* LEFT: searchable client list */}
        <Card className="h-fit sticky top-14">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Select a client
            </CardTitle>
            <CardDescription className="text-xs">
              {xeroClients.length} clients connected to Xero or QuickBooks
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-xs"
              />
            </div>

            {clientsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto -mx-1 divide-y">
                {filteredClients.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-4 text-center">
                    {search ? 'No matches' : 'No clients connected to accounting software'}
                  </p>
                ) : (
                  filteredClients.map(c => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedClient({ id: c.id, name: c.name })}
                      className={cn(
                        'w-full text-left px-2 py-2 text-xs rounded-sm transition-colors',
                        selectedClient?.id === c.id
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'hover:bg-muted/60',
                      )}
                    >
                      <div className="truncate">{c.name}</div>
                      {c.industry && <div className="text-[10px] text-muted-foreground truncate">{c.industry}</div>}
                    </button>
                  ))
                )}
              </div>
            )}

            {filteredClients.length > 0 && (
              <p className="text-[10px] text-muted-foreground text-center pt-1">
                Showing {filteredClients.length} of {xeroClients.length}
              </p>
            )}
          </CardContent>
        </Card>

        {/* RIGHT: detail */}
        <div className="space-y-4">
          {!selectedClient && (
            <Card>
              <CardContent className="pt-12 pb-12 text-center text-muted-foreground">
                <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium text-foreground">Pick a client to forecast</p>
                <p className="text-sm mt-1 max-w-md mx-auto">
                  Forecasts aren\'t pre-computed - with hundreds of clients, that would be hundreds of Xero API calls per page load.
                  Selecting a single client runs the forecast on demand in a few seconds.
                </p>
              </CardContent>
            </Card>
          )}

          {selectedClient && detailLoading && (
            <Card>
              <CardContent className="pt-12 pb-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Fetching 12 months of data for <span className="font-medium text-foreground">{selectedClient.name}</span>...
                </p>
                <p className="text-[10px] text-muted-foreground mt-1">Usually takes 5-15 seconds</p>
              </CardContent>
            </Card>
          )}

          {selectedClient && detailError && !detailLoading && (
            <Card>
              <CardContent className="pt-8 pb-8 text-center">
                <p className="text-sm text-red-600">Failed to load forecast</p>
                <p className="text-xs text-muted-foreground mt-1">The accounting connection may have expired. Try another client or reconnect.</p>
              </CardContent>
            </Card>
          )}

          {selectedClient && clientDetail && !detailLoading && (
            <>
              {insufficientData ? (
                <Card>
                  <CardContent className="pt-8 pb-8 text-center">
                    <p className="text-sm font-medium text-foreground">Insufficient accounting data for forecasting</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {clientDetail.historical?.length || 0} month(s) of data found. Need at least 3 months to produce a meaningful forecast.
                    </p>
                    {clientDetail.recommendations?.length > 0 && (
                      <div className="mt-4 space-y-1 text-left max-w-md mx-auto">
                        {clientDetail.recommendations.map((r: string, i: number) => (
                          <div key={i} className="flex items-start gap-2 text-xs">
                            <ArrowRight className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                            <p className="text-muted-foreground">{r}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-4 pb-4">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Avg Monthly Revenue</p>
                        <p className="text-xl font-bold">{fmtCurrency(clientDetail.summary.avgMonthlyRevenue)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 pb-4">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Avg Monthly Expenses</p>
                        <p className="text-xl font-bold">{fmtCurrency(clientDetail.summary.avgMonthlyExpenses)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 pb-4">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Avg Net Cash Flow</p>
                        <p className={cn('text-xl font-bold', clientDetail.summary.avgNetCashFlow >= 0 ? 'text-green-500' : 'text-red-500')}>
                          {fmtCurrency(clientDetail.summary.avgNetCashFlow)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 pb-4">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Trend</p>
                        <div className="flex items-center gap-2 mt-1">
                          {(() => {
                            const t = TREND_STYLES[clientDetail.summary.trend];
                            const Icon = t?.icon || Minus;
                            return (
                              <>
                                <Icon className={cn('h-5 w-5', t?.color)} />
                                <span className={cn('text-lg font-bold', t?.color)}>{t?.label}</span>
                                <span className="text-xs text-muted-foreground">({clientDetail.summary.revenueGrowthRate}%/mo)</span>
                              </>
                            );
                          })()}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Revenue vs Expenses */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Revenue vs Expenses (12mo history + 6mo forecast)</CardTitle>
                      <CardDescription className="text-xs">{selectedClient.name}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                          <Tooltip formatter={(v: number) => fmtCurrency(v)} />
                          <Legend />
                          <Bar dataKey="revenue" name="Revenue" fill="#22c55e" opacity={0.8} activeBar={false}/>
                          <Bar dataKey="expenses" name="Expenses" fill="#ef4444" opacity={0.6} activeBar={false}/>
                          {forecastStartLabel && (
                            <ReferenceLine
                              x={forecastStartLabel}
                              stroke="#5048e5"
                              strokeDasharray="4 4"
                              strokeWidth={1.5}
                              label={{ value: 'Forecast →', position: 'top', fill: '#5048e5', fontSize: 10, fontWeight: 600 }}
                            />
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Net Cash Flow with Confidence Band */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Net Cash Flow with Confidence Band</CardTitle>
                      <CardDescription className="text-xs">Shaded area shows projected range based on historical volatility</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                          <Tooltip formatter={(v: number) => fmtCurrency(v)} />
                          <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                          <Area dataKey="confidenceHigh" stackId="band" stroke="none" fill="#5048e5" fillOpacity={0.08} name="Upper Band" />
                          <Area dataKey="confidenceLow" stackId="band" stroke="none" fill="#ffffff" fillOpacity={1} name="Lower Band" />
                          <Line dataKey="net" stroke="#5048e5" strokeWidth={2} dot={{ r: 3 }} name="Net Cash Flow" />
                          {forecastStartLabel && (
                            <ReferenceLine
                              x={forecastStartLabel}
                              stroke="#5048e5"
                              strokeDasharray="4 4"
                              strokeWidth={1.5}
                              label={{ value: 'Forecast →', position: 'top', fill: '#5048e5', fontSize: 10, fontWeight: 600 }}
                            />
                          )}
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Recommendations */}
                  {clientDetail.recommendations?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">AI Recommendations</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {clientDetail.recommendations.map((r: string, i: number) => (
                            <div key={i} className="flex items-start gap-2">
                              <ArrowRight className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                              <p className="text-sm text-muted-foreground">{r}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setSelectedClient(null)}>Clear selection</Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
